import { Button } from '@/components/ui/button';
import { WhatsappLogo, Envelope, ArrowRight, FileText } from '@phosphor-icons/react';
import { CONTACT } from '@/lib/constants';

const GENERIC_WHATSAPP = `https://wa.me/${CONTACT.WHATSAPP_RAW}?text=${encodeURIComponent(
`Hello Aurea Digital Solutions.

I would like to learn more about the AWSAI Workflow Stability and AI Readiness Diagnostic.

Organisation:
Contact Person:
Email:
Phone:
Topic of Interest:

Please advise on the next steps.`
)}`;

const GENERIC_EMAIL = `mailto:${CONTACT.EMAIL}?subject=${encodeURIComponent(
  'AWSAI General Enquiry'
)}&body=${encodeURIComponent(
`Dear Aurea Digital Solutions,

I would like to learn more about the AWSAI Workflow Stability and AI Readiness Diagnostic.

Organisation:
Contact Person:
Email:
Phone:
Topic of Interest:

Please advise on the next steps.

Thank you.`
)}`;

export const ContactSection = ({ onStart }) => (
  <section className="py-20 lg:py-28 bg-slate-50" id="contact" data-testid="contact-section">
    <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
      <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-teal-700 mb-3">
        Get Started
      </div>
      <h2 className="text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-5">
        Start With a Free AWSAI Assessment
      </h2>
      <p className="text-slate-600 leading-relaxed mb-2 text-base">
        Begin with a short diagnostic assessment to understand your organisation&rsquo;s workflow
        stability and AI readiness. The assessment provides an initial score, maturity level, and
        high-level recommendation.
      </p>
      <p className="text-slate-600 leading-relaxed mb-8 text-[15px]">
        For deeper analysis, request the Professional Diagnostic Report or contact Aurea Digital
        Solutions to discuss suitable next steps.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          data-testid="contact-start-assessment-button"
          onClick={onStart}
          size="lg"
          className="bg-slate-900 text-white hover:bg-slate-800 font-semibold"
        >
          Start Free Assessment
          <ArrowRight size={18} weight="bold" className="ml-2" />
        </Button>
        <Button asChild size="lg" className="bg-amber-300 text-slate-900 hover:bg-amber-200 font-semibold">
          <a href="#pricing" data-testid="contact-request-report-button">
            <FileText size={18} weight="bold" className="mr-2" />
            Request Professional Report
          </a>
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
        <Button asChild size="lg" variant="outline" className="border-2 border-slate-300 text-slate-700 hover:bg-white">
          <a href={GENERIC_WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button">
            <WhatsappLogo size={18} weight="fill" className="mr-2 text-green-600" />
            WhatsApp Aurea
          </a>
        </Button>
        <Button asChild size="lg" variant="outline" className="border-2 border-slate-300 text-slate-700 hover:bg-white">
          <a href={GENERIC_EMAIL} data-testid="contact-email-button">
            <Envelope size={18} weight="bold" className="mr-2" />
            Email Aurea
          </a>
        </Button>
      </div>
      <div className="text-xs text-slate-500 mt-6">
        {CONTACT.COMPANY} · {CONTACT.WHATSAPP_NUMBER} · {CONTACT.EMAIL}
      </div>
    </div>
  </section>
);

export const SiteFooter = () => (
  <footer className="bg-slate-900 text-slate-400 py-14" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <div className="text-white font-bold text-base mb-2">AWSAI Framework</div>
          <div className="text-sm text-slate-300">Workflow Stability and AI Readiness Diagnostic</div>
          <div className="text-sm text-slate-400 mt-1">SMART-Workflow Methodology</div>
        </div>
        <div>
          <div className="text-white font-semibold text-sm mb-2 tracking-wide">Contact</div>
          <div className="text-sm text-slate-300">{CONTACT.COMPANY}</div>
          <a href={`mailto:${CONTACT.EMAIL}`} className="text-sm text-slate-300 hover:text-white block mt-1">
            {CONTACT.EMAIL}
          </a>
          <div className="text-sm text-slate-300 mt-1">{CONTACT.WHATSAPP_NUMBER}</div>
        </div>
        <div>
          <div className="text-white font-semibold text-sm mb-2 tracking-wide">Engage</div>
          <a href="#pricing" className="block text-sm text-slate-300 hover:text-white">What You Receive</a>
          <a href="#advisory" className="block text-sm text-slate-300 hover:text-white mt-1">Advisory Services</a>
          <a href="#advisor" className="block text-sm text-slate-300 hover:text-white mt-1">Talk to an Advisor</a>
        </div>
      </div>
      <div className="border-t border-slate-800 mt-10 pt-6 text-xs text-slate-500">
        &copy; {new Date().getFullYear()} {CONTACT.COMPANY}. All rights reserved.
        AWSAI Framework and SMART-Workflow Methodology are proprietary diagnostic instruments.
      </div>
    </div>
  </footer>
);
